import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { goldPriceSeries } from '@/mocks/marketSeries';
import { tradingSummary } from '@/utils/tradingSummary';
import CountUp from '@/components/base/CountUp';
import Reveal from '@/components/base/Reveal';

const stats = [
  { label: 'Recorded Trades', value: tradingSummary.totalTrades, suffix: '' },
  { label: 'Instruments Traded', value: 1, suffix: '' },
  { label: 'XAUUSD Share', value: 100, suffix: '%' },
];

function MarketScope() {
  return (
    <section
      id="market-scope"
      className="relative w-full overflow-hidden border-y border-background-200 bg-background-100"
    >
      <div className="absolute inset-0 opacity-[0.3] pointer-events-none">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={goldPriceSeries} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="scopeGoldFade" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="oklch(var(--primary-500))" stopOpacity={0.3} />
                <stop offset="100%" stopColor="oklch(var(--primary-500))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="price"
              stroke="oklch(var(--primary-500))"
              strokeWidth={2}
              fill="url(#scopeGoldFade)"
              dot={false}
              isAnimationActive
              animationDuration={2400}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="relative w-full px-4 md:px-8 lg:px-12 py-16 md:py-24 max-w-[1440px] mx-auto">
        <Reveal>
          <p className="text-[11px] font-data tracking-[0.24em] text-primary-600 mb-5">
            MARKET SCOPE
          </p>
          <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[32px] leading-[1.08] sm:text-[44px] lg:text-[50px]">
            Focused On One Market.
          </h2>
          <p className="mt-5 text-[15px] md:text-base leading-relaxed text-foreground-600 max-w-[620px]">
            Every recorded position the system takes is on {tradingSummary.instrument} — gold
            against the US dollar — and nothing else. There is no watchlist of symbols behind it.
          </p>
          <p className="mt-4 text-[15px] md:text-base leading-relaxed text-foreground-600 max-w-[620px]">
            That focus is deliberate. Gold trends cleanly, reacts to a short list of well-known
            drivers — the dollar, interest rates, risk sentiment — and behaves differently from one
            session to the next. Studying a single market closely beats spreading the same rules
            thin across dozens of pairs: what the framework learns stays specific to gold instead of
            averaged out across everything.
          </p>
          <div className="mt-6 flex items-start gap-2 max-w-[620px]">
            <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-close-line text-[14px] text-foreground-400"></i>
            </span>
            <span className="text-[12.5px] leading-snug text-foreground-500">
              It does not scan a basket of instruments or rotate into whatever is moving. When gold
              gives no valid setup, it simply waits.
            </span>
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-14 items-center">
          <Reveal delay={120}>
            <p className="font-heading font-extrabold tracking-tighter text-foreground-950/90 text-[68px] leading-none sm:text-[120px] lg:text-[150px] -ml-1">
              XAUUSD
            </p>

            <div className="mt-8">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-[12px] font-data tracking-[0.14em] text-foreground-700">
                  {tradingSummary.instrument}
                </span>
                <span className="text-[12px] font-data text-primary-700">
                  {tradingSummary.instrumentShare}
                </span>
              </div>
              <div className="w-full h-2.5 rounded-full bg-background-300/70 overflow-hidden">
                <div className="h-full w-full rounded-full bg-primary-500"></div>
              </div>
              <div className="mt-2.5 flex items-center justify-between">
                <span className="text-[11px] text-foreground-500">Instrument share of activity</span>
                <span className="text-[11px] font-data text-foreground-400">ALL RECORDED TRADES</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={200}>
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg border border-background-300 bg-background-50 px-5 py-5 flex items-center justify-between"
                >
                  <span className="text-[13px] text-foreground-600">{stat.label}</span>
                  <span className="font-data text-[26px] md:text-[30px] font-semibold text-foreground-950 leading-none">
                    <CountUp value={stat.value} suffix={stat.suffix} />
                  </span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default MarketScope;